/**
 * Defensive readers for worker payloads: screening configs (admin-edited JSON)
 * and `lead.ai_fields.screening`. Fills gaps so a partial payload still renders.
 */
import { jsonObject, type LeadDetail, type Qualification } from '@/features/pipeline/types';
import {
  EMPTY_CONFIG,
  RULE_OPS,
  type KnockoutRule,
  type QuestionType,
  type RuleOp,
  type RuleValue,
  type ScoringRule,
  type ScreeningConfig,
  type ScreeningConfigs,
  type ScreeningFields,
  type ScreeningQuestion,
} from './types';

const TYPES: ReadonlySet<string> = new Set<QuestionType>(['text', 'number', 'boolean', 'choice']);
const OPS: ReadonlySet<string> = new Set(RULE_OPS.map((o) => o.value));

function rec(v: unknown): Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

function ruleValue(v: unknown): RuleValue {
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) return v.filter((x): x is string | number | boolean => ['string', 'number', 'boolean'].includes(typeof x));
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  return String(v);
}

function question(v: unknown): ScreeningQuestion | null {
  const q = rec(v);
  if (typeof q.key !== 'string' || !q.key) return null;
  const type: QuestionType = TYPES.has(String(q.type)) ? (q.type as QuestionType) : 'text';
  const out: ScreeningQuestion = {
    key: q.key,
    prompt: typeof q.prompt === 'string' ? q.prompt : '',
    type,
    required: q.required === true || q.required === 1,
  };
  if (type === 'choice') out.choices = Array.isArray(q.choices) ? q.choices.map(String) : [];
  return out;
}

function knockout(v: unknown): KnockoutRule | null {
  const r = rec(v);
  if (typeof r.field !== 'string' || !OPS.has(String(r.op))) return null;
  return { field: r.field, op: r.op as RuleOp, value: ruleValue(r.value), reason: typeof r.reason === 'string' ? r.reason : '' };
}

function scoring(v: unknown): ScoringRule | null {
  const r = rec(v);
  if (typeof r.field !== 'string' || !OPS.has(String(r.op))) return null;
  return { field: r.field, op: r.op as RuleOp, value: ruleValue(r.value), points: num(r.points) };
}

export function normalizeConfig(raw: unknown): ScreeningConfig {
  const c = rec(raw);
  const rules = rec(c.rules);
  return {
    draft: c.draft === true || c.draft === 1,
    questions: (Array.isArray(c.questions) ? c.questions : []).map(question).filter((q): q is ScreeningQuestion => !!q),
    rules: {
      knockouts: (Array.isArray(rules.knockouts) ? rules.knockouts : []).map(knockout).filter((r): r is KnockoutRule => !!r),
      scoring: (Array.isArray(rules.scoring) ? rules.scoring : []).map(scoring).filter((r): r is ScoringRule => !!r),
      qualified_min: num(rules.qualified_min, EMPTY_CONFIG.rules.qualified_min),
      review_min: num(rules.review_min, EMPTY_CONFIG.rules.review_min),
    },
  };
}

/** `GET /api/settings/screening` may answer `{ [slug]: config }` or `{ screening: {...} }`. */
export function normalizeConfigs(raw: unknown): ScreeningConfigs {
  const r = rec(raw);
  const source = 'screening' in r && typeof r.screening === 'object' ? rec(r.screening) : r;
  const out: ScreeningConfigs = {};
  for (const [slug, cfg] of Object.entries(source)) out[slug] = normalizeConfig(cfg);
  return out;
}

const RESULTS: ReadonlySet<string> = new Set(['qualified', 'needs_review', 'not_qualified']);

/** `ai_fields.screening`, or null when no call has been analysed. */
export function screeningFields(lead: Pick<LeadDetail, 'ai_fields_json'>): ScreeningFields | null {
  const s = jsonObject(lead.ai_fields_json).screening;
  if (!s || typeof s !== 'object' || Array.isArray(s)) return null;
  const r = s as Record<string, unknown>;
  const result = RESULTS.has(String(r.result)) ? (r.result as Exclude<Qualification, null>) : null;
  return {
    answers: rec(r.answers),
    result,
    score: typeof r.score === 'number' && Number.isFinite(r.score) ? r.score : null,
    max_score: typeof r.max_score === 'number' && Number.isFinite(r.max_score) && r.max_score > 0 ? r.max_score : null,
    reasons: Array.isArray(r.reasons) ? r.reasons.filter((x): x is string => typeof x === 'string' && x.trim() !== '') : [],
    call_id: typeof r.call_id === 'string' ? r.call_id : null,
  };
}
